import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  HiOutlineUserPlus, HiOutlineEye, HiOutlineEyeSlash,
  HiOutlineEnvelope, HiOutlineLockClosed, HiOutlineUser, HiOutlineBuildingStorefront,
  HiOutlineBriefcase, HiOutlineCurrencyPound, HiOutlineCurrencyEuro,
  HiOutlinePencilSquare, HiOutlineArrowLeftCircle, HiOutlineLockClosed as HiOutlineLock
} from 'react-icons/hi2';
import api from '../utils/api';
import TopBar from '../components/TopBar';
import { useRestaurant } from '../context/RestaurantContext';

/* ============ Locked Restaurant Display ============ */
/* Users are managed PER-RESTAURANT. When creating or editing,
   the restaurant is always the currently active one and cannot be changed.
   Moving users across restaurants would orphan their shift history. */
const LockedRestaurant = ({ restaurant }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px',
    background: 'var(--paper-soft)',
    border: '1.5px solid var(--border)',
    borderRadius: 'var(--r)',
    minHeight: 56
  }}>
    {restaurant?.logo ? (
      <img src={restaurant.logo} alt=""
        style={{ width: 36, height: 36, borderRadius: 9, objectFit: 'cover', flexShrink: 0 }} />
    ) : (
      <div style={{
        width: 36, height: 36, borderRadius: 9,
        background: 'var(--ink-100)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0
      }}>
        <HiOutlineBuildingStorefront size={18} color="var(--text-muted)" />
      </div>
    )}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontWeight: 700, fontSize: 14, color: 'var(--text)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
      }}>
        {restaurant?.name || 'No restaurant'}
      </div>
      {restaurant?.location && (
        <div style={{
          fontSize: 12, color: 'var(--text-muted)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
        }}>
          {restaurant.location}
        </div>
      )}
    </div>
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, fontWeight: 700,
      padding: '4px 8px',
      background: 'var(--lime-100)',
      color: 'var(--lime-700)',
      borderRadius: 999,
      letterSpacing: '0.05em', textTransform: 'uppercase',
      flexShrink: 0
    }}>
      <HiOutlineLock size={11} /> Locked
    </div>
  </div>
);

const CurrencyToggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 8 }}>
    <CurrencyOption active={value === 'GBP'} onClick={() => onChange('GBP')}
      icon={<HiOutlineCurrencyPound size={16} />} label="Pounds (£)" />
    <CurrencyOption active={value === 'EUR'} onClick={() => onChange('EUR')}
      icon={<HiOutlineCurrencyEuro size={16} />} label="Euro (€)" />
  </div>
);

const CurrencyOption = ({ active, onClick, icon, label }) => (
  <button type="button" onClick={onClick}
    style={{
      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      padding: '12px 14px',
      background: active ? 'var(--blue-500)' : 'var(--surface)',
      color: active ? '#fff' : 'var(--text)',
      border: `1.5px solid ${active ? 'var(--blue-500)' : 'var(--border-strong)'}`,
      borderRadius: 'var(--r)',
      cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
      transition: 'all 0.15s'
    }}>
    {icon} {label}
  </button>
);

const RolePill = ({ active, accent, onClick, children }) => (
  <button type="button" onClick={onClick}
    style={{
      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '12px 14px',
      background: active ? accent : 'var(--surface)',
      color: active ? '#fff' : 'var(--text)',
      border: `1.5px solid ${active ? accent : 'var(--border-strong)'}`,
      borderRadius: 'var(--r)',
      cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
      transition: 'all 0.15s'
    }}>{children}</button>
);

/* ============ Main Form Page ============ */
const UserForm = () => {
  const nav = useNavigate();
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(userId);
  const { activeRestaurant, restaurants } = useRestaurant();

  const roleParam = searchParams.get('role');
  const initialRole = roleParam === 'manager' ? 'manager' : 'employee';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // When creating: restaurant is ALWAYS the currently-active one.
  // When editing: pre-filled from the user's existing restaurant (also locked).
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: initialRole,
    restaurantId: activeRestaurant?._id || '',
    ratePerHour: initialRole === 'manager' ? 15 : 10,
    currency: 'GBP'
  });

  // Safety: if creating but no active restaurant, redirect to picker.
  // RequireRestaurant in App.js handles this at the route level, but keep a belt-and-braces here.
  useEffect(() => {
    if (!isEdit && !activeRestaurant?._id) {
      toast.error('Pick a restaurant first');
      nav('/restaurants');
    }
  }, [isEdit, activeRestaurant?._id, nav]);

  // If active restaurant changes (e.g. owner switches restaurants in another tab),
  // sync the form's restaurantId so we always create in the current restaurant.
  useEffect(() => {
    if (!isEdit && activeRestaurant?._id && form.restaurantId !== activeRestaurant._id) {
      setForm(p => ({ ...p, restaurantId: activeRestaurant._id }));
    }
  }, [activeRestaurant?._id, isEdit, form.restaurantId]);

  // Load user when editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api.get('/users', {
          params: activeRestaurant?._id ? { restaurantId: activeRestaurant._id } : {}
        });
        const u = (r.data.users || []).find(x => x._id === userId);
        if (!u) {
          toast.error('User not found in this restaurant');
          nav('/users');
          return;
        }
        setForm({
          name: u.name || '',
          email: u.email || '',
          password: '',
          role: u.role === 'user' ? 'employee' : u.role,
          restaurantId: u.restaurant?._id || activeRestaurant?._id || '',
          ratePerHour: u.employeeRecord?.ratePerHour ?? '',
          currency: u.employeeRecord?.currency || 'GBP'
        });
      } catch {
        toast.error('Failed to load user');
        nav('/users');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, userId, nav, activeRestaurant?._id]);

  const isManager = form.role === 'manager';
  const CurrencyIcon = form.currency === 'EUR' ? HiOutlineCurrencyEuro : HiOutlineCurrencyPound;

  // The restaurant shown in the locked field:
  // - Creating  → current active restaurant
  // - Editing   → the user's own restaurant (matched from the restaurants list, or null fallback)
  const displayedRestaurant = isEdit
    ? restaurants.find(r => r._id === form.restaurantId) || null
    : activeRestaurant;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Fill all required fields');
    if (!isEdit && (!form.password || form.password.length < 6)) {
      return toast.error('Password must be 6+ characters');
    }
    if (isEdit && form.password && form.password.length < 6) {
      return toast.error('New password must be 6+ characters');
    }
    if (!form.restaurantId) return toast.error('No restaurant selected');

    setSaving(true);
    try {
      // When CREATING: try the typed email first. If it's already in use,
      // append 3 random digits to the local-part and retry up to 5 times.
      // When EDITING: just attempt once — duplicate-email errors should bubble
      // up so the owner can fix the value manually.
      let emailToTry = form.email;
      let attempt = 0;
      let lastError = null;
      let suffixed = false;

      while (attempt < 5) {
        const payload = {
          name: form.name,
          email: emailToTry,
          role: form.role,
          restaurantId: form.restaurantId,
          ratePerHour: form.ratePerHour === '' ? 0 : parseFloat(form.ratePerHour) || 0,
          currency: form.currency
        };
        if (form.password) payload.password = form.password;

        try {
          if (isEdit) {
            await api.put(`/users/${userId}`, payload);
          } else {
            await api.post('/users', payload);
          }

          if (suffixed) {
            toast.success(`Created with email ${emailToTry} (auto-suffix added)`);
          } else {
            toast.success(isEdit ? 'User updated' : 'User created');
          }
          nav('/users');
          return;
        } catch (err) {
          lastError = err;
          const msg = (err.response?.data?.message || '').toLowerCase();

          // Only retry on a duplicate-email response, only when CREATING
          if (!isEdit && msg.includes('email already in use')) {
            const [local, domain] = form.email.split('@');
            const suffix = Math.floor(Math.random() * 900 + 100); // 100..999
            emailToTry = `${local}${suffix}@${domain}`;
            // Reflect the new email in the form so the owner can see it
            setForm(p => ({ ...p, email: emailToTry }));
            suffixed = true;
            attempt++;
            continue;
          }
          throw err;
        }
      }
      toast.error(lastError?.response?.data?.message || 'Could not find a unique email after 5 tries');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <TopBar title={isEdit ? 'Edit User' : 'New User'} />
        <div className="empty">Loading…</div>
      </div>
    );
  }

  const heroIcon = isEdit
    ? <HiOutlinePencilSquare size={26} />
    : isManager ? <HiOutlineBriefcase size={26} /> : <HiOutlineUserPlus size={26} />;
  const heroTitle = isEdit
    ? `Edit ${form.name || 'User'}`
    : isManager ? 'Add Manager' : 'Add Employee';
  const heroSub = isEdit
    ? `Update details, role, hourly rate and currency. Restaurant assignment is locked.`
    : isManager
      ? `Create a manager for ${activeRestaurant?.name || 'this restaurant'}. Managers add employees and shifts. They cannot access user management or salary reports.`
      : `Create an employee for ${activeRestaurant?.name || 'this restaurant'}. They can sign in and view their own shifts and earnings.`;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar title={isEdit ? 'Edit User' : 'New User'} />

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, var(--ink-900) 0%, var(--ink-800) 100%)',
        color: '#fff',
        padding: '32px 24px 76px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background:
            'radial-gradient(ellipse 60% 50% at 95% -20%, rgba(155, 242, 19, 0.14) 0%, transparent 60%),' +
            'radial-gradient(ellipse 60% 50% at 0% 120%, rgba(28, 92, 255, 0.18) 0%, transparent 55%)',
          pointerEvents: 'none'
        }} />
        <div style={{ maxWidth: 720, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <button onClick={() => nav('/users')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.08)', color: '#fff',
              border: '1.5px solid rgba(255,255,255,0.16)',
              padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', marginBottom: 18,
              transition: 'background 0.15s'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          >
            <HiOutlineArrowLeftCircle size={14} /> Back to users
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: isManager ? 'var(--blue-500)' : 'var(--lime-400)',
              color: isManager ? '#fff' : 'var(--ink-900)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              boxShadow: isManager
                ? '0 0 0 4px rgba(28,92,255,0.18), 0 6px 24px rgba(28,92,255,0.4)'
                : '0 0 0 4px rgba(155,242,19,0.18), 0 6px 24px rgba(155,242,19,0.4)'
            }}>
              {heroIcon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                margin: 0, fontSize: 28, fontWeight: 800,
                letterSpacing: '-0.025em', color: '#fff', lineHeight: 1.1
              }}>{heroTitle}</h1>
              <p style={{
                margin: '6px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.5, maxWidth: 540
              }}>{heroSub}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form card overlapping hero */}
      <div className="page" style={{ marginTop: -40, position: 'relative', zIndex: 2, paddingTop: 0 }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <form onSubmit={submit} className="card" style={{ padding: 28 }}>

            {/* Role pills */}
            <div className="field" style={{ marginBottom: 18 }}>
              <label className="label">{isEdit ? 'Role' : 'Account type'}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <RolePill active={form.role === 'manager'} accent="var(--blue-500)"
                  onClick={() => setForm(p => ({ ...p, role: 'manager', ratePerHour: isEdit ? p.ratePerHour : 15 }))}>
                  <HiOutlineBriefcase size={14} /> Manager
                </RolePill>
                <RolePill active={form.role === 'employee'} accent="var(--lime-700)"
                  onClick={() => setForm(p => ({ ...p, role: 'employee', ratePerHour: isEdit ? p.ratePerHour : 10 }))}>
                  <HiOutlineUser size={14} /> Employee
                </RolePill>
              </div>
            </div>

            {/* Locked restaurant — shown at the top so it's the first thing the owner sees */}
            <div className="field" style={{ marginBottom: 18 }}>
              <label className="label">
                Restaurant
                <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> (this user belongs to)</span>
              </label>
              <LockedRestaurant restaurant={displayedRestaurant} />
            </div>

            {/* Two-column on desktop */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16
            }}>
              <div className="field">
                <label className="label">Full name</label>
                <div className="field-shell">
                  <span className="field-shell-icon"><HiOutlineUser /></span>
                  <input type="text" placeholder="e.g. Sara Khan" value={form.name}
                    onChange={e => {
                      const name = e.target.value;
                      const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '');
                      setForm(p => {
                        const next = { ...p, name };
                        if (!isEdit) {
                          const prevSlug = p.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                          const wasAuto = !p.email || p.email === `${prevSlug}@gmail.com`;
                          if (wasAuto) next.email = slug ? `${slug}@gmail.com` : '';
                          if (!p.password || p.password === '112233') next.password = '112233';
                        }
                        return next;
                      });
                    }}
                    required />
                </div>
              </div>

              <div className="field">
                <label className="label">Email address</label>
                <div className="field-shell">
                  <span className="field-shell-icon"><HiOutlineEnvelope /></span>
                  <input type="email" placeholder="sara@example.com" value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
                </div>
              </div>
            </div>

            <div className="field" style={{ marginTop: 16 }}>
              <label className="label">
                {isEdit
                  ? <>New password <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(leave blank to keep current)</span></>
                  : <>Password <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(min 6 chars)</span></>
                }
              </label>
              <div className="field-shell">
                <span className="field-shell-icon"><HiOutlineLockClosed /></span>
                <input type={showPw ? 'text' : 'password'}
                  placeholder={isEdit ? 'Leave empty to keep unchanged' : 'Set initial password'}
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  required={!isEdit} minLength={6} />
                <button type="button" className="field-shell-action" onClick={() => setShowPw(s => !s)} tabIndex={-1}>
                  {showPw ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                </button>
              </div>
            </div>

            {/* Currency + rate side by side */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginTop: 16
            }}>
              <div className="field">
                <label className="label">Currency</label>
                <CurrencyToggle value={form.currency}
                  onChange={(c) => setForm(p => ({ ...p, currency: c }))} />
              </div>

              <div className="field">
                <label className="label">
                  Hourly rate
                  {isManager && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> (optional)</span>}
                </label>
                <div className="field-shell">
                  <span className="field-shell-icon"><CurrencyIcon /></span>
                  <input type="number" min="0" step="0.5"
                    placeholder={isManager ? 'e.g. 15.00' : '10.00'}
                    value={form.ratePerHour}
                    onChange={e => setForm(p => ({ ...p, ratePerHour: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* Action bar */}
            <div style={{
              display: 'flex', gap: 10, justifyContent: 'flex-end',
              marginTop: 28, paddingTop: 22, borderTop: '1px solid var(--border)',
              flexWrap: 'wrap'
            }}>
              <button type="button" className="btn btn-ghost" onClick={() => nav('/users')}>
                Cancel
              </button>
              <button type="submit"
                className={isManager ? 'btn btn-primary' : 'btn btn-accent'}
                disabled={saving}
                style={{ minWidth: 180 }}>
                {saving
                  ? 'Saving…'
                  : isEdit
                    ? 'Save changes'
                    : `Create ${isManager ? 'manager' : 'employee'}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;